import { StateSelect } from "../components/StateSelect";
/**
 * Customers — who the business works for (CRM-1), and bringing the list
 * across from ServiceM8 or anything else that exports a CSV (CRM-2).
 *
 * Adding a customer takes its first site and contact in the same form,
 * because a customer with no site can't have a job, and the office is
 * usually on the phone to the person when they add them.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import { ApiError, api } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { money, titleCase } from "../lib/format";
import { Card, Chip, Empty, ErrorState, Loading, PageHeader } from "../components/ui";
import type { CustomerIn, CustomerType, ImportResult } from "../lib/types";

export const CUSTOMER_TYPES: CustomerType[] = ["commercial", "residential", "strata", "government", "builder"];

export default function Customers() {
  const { can } = useAuth();
  const isOffice = can(...OFFICE);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const q = search.trim();

  const list = useQuery({
    queryKey: ["customers", q],
    queryFn: () => api.customers.list(q || undefined),
    placeholderData: (previous) => previous,
  });

  return (
    <div className="page">
      <PageHeader
        title="Customers"
        actions={
          isOffice && (
            <div className="btn-row">
              <button className="btn" type="button" onClick={() => setImporting((v) => !v)}>
                Import a CSV
              </button>
              <button className="btn btn-primary" type="button" onClick={() => setAdding((v) => !v)}>
                Add a customer
              </button>
            </div>
          )
        }
      >
        Who you work for, where their buildings are, and who to ring.
      </PageHeader>

      {adding && <AddCustomer onCancel={() => setAdding(false)} />}
      {importing && <ImportCard onDone={() => setImporting(false)} />}

      <div className="toolbar">
        <label className="search">
          <input
            type="search"
            value={search}
            placeholder="Name, suburb, street, contact or email"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {list.isPending ? (
        <Loading what="Loading customers" />
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.length === 0 ? (
        <Card>
          <Empty title={q ? "Nobody matches that" : "No customers yet"}>
            {q ? "Try part of the name, a suburb or a contact's email." : "Add one, or import the list from ServiceM8."}
          </Empty>
        </Card>
      ) : (
        <Card padded={false}>
          {list.data.map((c) => (
            <Link key={c.id} to={`/customers/${c.id}`} className="inv-row">
              <div>
                <b>{c.name}</b>{" "}
                {c.on_credit_hold && <Chip tone="crit">Credit hold</Chip>}
                <div className="tiny muted">
                  {[c.primary_contact, c.primary_phone, c.primary_email].filter(Boolean).join(" · ") || titleCase(c.type)}
                </div>
              </div>
              <div className="right tiny">
                {c.site_count} site{c.site_count === 1 ? "" : "s"}
                {c.open_job_count > 0 && ` · ${c.open_job_count} open job${c.open_job_count === 1 ? "" : "s"}`}
                {c.owing_cents > 0 && <div className="mono">{money(c.owing_cents)} owing</div>}
              </div>
            </Link>
          ))}
        </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ adding */

function AddCustomer({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: "",
    type: "commercial" as CustomerType,
    abn: "",
    street: "",
    suburb: "",
    state: "SA",
    postcode: "",
    contact: "",
    phone: "",
    email: "",
  });
  const [twin, setTwin] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  const create = useMutation({
    mutationFn: (allowDuplicate: boolean) => {
      const body: CustomerIn = {
        name: form.name.trim(),
        type: form.type,
        abn: form.type === "residential" ? null : form.abn.trim() || null,
        allow_duplicate: allowDuplicate,
      };
      if (form.street.trim() || form.suburb.trim()) {
        body.site = { address_line: form.street, suburb: form.suburb, state: form.state, postcode: form.postcode };
      }
      if (form.contact.trim() || form.phone.trim() || form.email.trim()) {
        body.contact = { name: form.contact.trim() || form.name.trim(), phone: form.phone, email: form.email };
      }
      return api.customers.create(body);
    },
    onSuccess: (c) => {
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
      void queryClient.invalidateQueries({ queryKey: ["sites"] });
      navigate(`/customers/${c.id}`);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setTwin(err.message);
        return;
      }
      setProblem(err instanceof ApiError ? err.message : "Couldn't add them.");
    },
  });

  return (
    <Card title="Add a customer" subtitle="Their first site and contact can go in now, or later">
      <div className="form-grid">
        <label className="field">
          <span>Name</span>
          <input value={form.name} onChange={set("name")} autoFocus />
        </label>
        <label className="field">
          <span>Type</span>
          <select value={form.type} onChange={set("type")}>
            {CUSTOMER_TYPES.map((t) => (
              <option key={t} value={t}>
                {titleCase(t)}
              </option>
            ))}
          </select>
        </label>
        {form.type !== "residential" && (
        <label className="field">
          <span>ABN (optional)</span>
          <input value={form.abn} onChange={set("abn")} inputMode="numeric" />
        </label>
        )}
      </div>
      <h3 className="tiny muted" style={{ margin: "14px 0 6px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        Site — where the work is
      </h3>
      <div className="form-grid">
        <label className="field">
          <span>Street</span>
          <input value={form.street} onChange={set("street")} />
        </label>
        <label className="field">
          <span>Suburb</span>
          <input value={form.suburb} onChange={set("suburb")} />
        </label>
        <label className="field">
          <span>State</span>
          <StateSelect value={form.state} onChange={set("state")} />
        </label>
        <label className="field">
          <span>Postcode</span>
          <input value={form.postcode} onChange={set("postcode")} inputMode="numeric" />
        </label>
      </div>
      <h3 className="tiny muted" style={{ margin: "14px 0 6px", textTransform: "uppercase", letterSpacing: "0.08em" }}>
        Contact
      </h3>
      <div className="form-grid">
        <label className="field">
          <span>Name</span>
          <input value={form.contact} onChange={set("contact")} />
        </label>
        <label className="field">
          <span>Phone</span>
          <input value={form.phone} onChange={set("phone")} inputMode="tel" />
        </label>
        <label className="field">
          <span>Email</span>
          <input value={form.email} onChange={set("email")} type="email" autoCapitalize="none" />
        </label>
      </div>
      {twin && (
        <div className="notice notice-warn" style={{ marginTop: 10 }}>
          {twin}{" "}
          <button className="linkish" type="button" onClick={() => create.mutate(true)}>
            Add anyway
          </button>
        </div>
      )}
      {problem && (
        <p className="notice notice-crit" role="alert" style={{ marginTop: 10 }}>
          {problem}
        </p>
      )}
      <div className="btn-row" style={{ marginTop: 12 }}>
        <button
          className="btn btn-primary"
          type="button"
          disabled={!form.name.trim() || create.isPending}
          onClick={() => {
            setTwin(null);
            setProblem(null);
            create.mutate(false);
          }}
        >
          {create.isPending ? "Adding…" : "Add customer"}
        </button>
        <button className="btn" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Card>
  );
}

/* ---------------------------------------------------------------- importing */

const FIELD_LABEL: Record<string, string> = {
  name: "Name",
  abn: "ABN",
  email: "Email",
  phone: "Phone",
  mobile: "Mobile",
  first_name: "First name",
  last_name: "Last name",
  contact: "Contact",
  billing_address: "Billing address",
  address: "Address",
  street: "Street",
  suburb: "Suburb",
  state: "State",
  postcode: "Postcode",
  notes: "Notes",
  type: "Type",
  terms: "Terms",
};

/** Preview first, then the same file for real (CRM-2). */
function ImportCard({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [done, setDone] = useState<ImportResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: ({ f, dry }: { f: File; dry: boolean }) => api.customers.importCsv(f, dry),
    onSuccess: (result) => {
      setProblem(null);
      if (result.dry_run) {
        setPreview(result);
      } else {
        setDone(result);
        setPreview(null);
        void queryClient.invalidateQueries({ queryKey: ["customers"] });
        void queryClient.invalidateQueries({ queryKey: ["sites"] });
      }
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't read that file."),
  });

  const creating = preview?.rows.filter((r) => r.action === "create").length ?? 0;

  return (
    <Card title="Import customers" subtitle="From ServiceM8, Simpro, Xero or a spreadsheet — saved as CSV">
      {done ? (
        <>
          <p className="notice notice-info">
            Added {done.created} customer{done.created === 1 ? "" : "s"}
            {done.duplicates ? `; skipped ${done.duplicates} already here` : ""}
            {done.errors ? `; ${done.errors} row${done.errors === 1 ? "" : "s"} had no name` : ""}.
          </p>
          <div className="btn-row">
            <button className="btn" type="button" onClick={onDone}>
              Done
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="tiny muted" style={{ marginTop: 0 }}>
            In ServiceM8, export your client list as CSV, then choose it here. You'll see what Keystone makes of every
            row before anything is added, and anyone already here is skipped — so importing the same file twice is safe.
          </p>
          <div className="btn-row">
            <label className="btn">
              {file ? file.name : "Choose a CSV file"}
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                  setPreview(null);
                  if (f) run.mutate({ f, dry: true });
                  e.target.value = "";
                }}
              />
            </label>
            <button className="btn" type="button" onClick={onDone}>
              Cancel
            </button>
          </div>
          {run.isPending && <p className="tiny muted">Reading…</p>}
          {problem && (
            <p className="notice notice-crit" role="alert" style={{ marginTop: 10 }}>
              {problem}
            </p>
          )}
          {preview && (
            <div style={{ marginTop: 12 }}>
              <p className="tiny">
                <b>Using:</b>{" "}
                {Object.entries(preview.matched)
                  .map(([k, col]) => `${col} → ${FIELD_LABEL[k] ?? k}`)
                  .join(", ")}
                {preview.ignored.length > 0 && (
                  <>
                    <br />
                    <b>Not used:</b> {preview.ignored.join(", ")}
                  </>
                )}
              </p>
              <div className="table-scroll" style={{ maxHeight: 360, overflowY: "auto" }}>
                <table className="tiny">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Customer</th>
                      <th>Site</th>
                      <th>Contact</th>
                      <th>What happens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r) => (
                      <tr key={r.line}>
                        <td className="mono muted">{r.line}</td>
                        <td>{r.name || <span className="muted">—</span>}</td>
                        <td>{r.site ?? <span className="muted">none</span>}</td>
                        <td>{[r.contact, r.email, r.phone].filter(Boolean).join(" · ") || <span className="muted">—</span>}</td>
                        <td>
                          {r.action === "create" ? (
                            <Chip tone="ok">Add</Chip>
                          ) : r.action === "duplicate" ? (
                            <Chip tone="neutral">Skip — {r.reason}</Chip>
                          ) : (
                            <Chip tone="crit">{r.reason}</Chip>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="btn-row" style={{ marginTop: 10 }}>
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={!file || creating === 0 || run.isPending}
                  onClick={() => file && run.mutate({ f: file, dry: false })}
                >
                  {run.isPending ? "Importing…" : `Add ${creating} customer${creating === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
